(() => {
  const config = window.BUYLIST_CONFIG || {};
  const customer = config.customer || window.BUYLIST_CUSTOMER || null;
  const normalizeBaseUrl = (value, fallback = '/tools/cc_buylist') => String(value || fallback).replace(/\/+$/, '');
  const API_BASE_URL = normalizeBaseUrl(config.apiBaseUrl || config.api_base_url);
  const GAMES_ENDPOINT = config.gamesEndpoint || config.games_endpoint || `${API_BASE_URL}/buylist/games`;
  const SETS_ENDPOINT = config.setsEndpoint || config.sets_endpoint || `${API_BASE_URL}/buylist/sets`;
  const SEARCH_ENDPOINT = config.searchEndpoint || config.search_endpoint || `${API_BASE_URL}/buylist/search`;
  const PRICE_ENDPOINT = config.priceEndpoint || config.price_endpoint || `${API_BASE_URL}/buylist/search/prices`;
  const SUBMIT_ENDPOINT = config.submitEndpoint || config.submit_endpoint || `${API_BASE_URL}/buylist/submit`;
  const CONTENT_BASE_URL = normalizeBaseUrl(config.contentBaseUrl || config.content_base_url || 'https://content.cardcompanion.co.uk');
  const STORAGE_VERSION = 3;
  const SET_SEARCH_DEBOUNCE_MS = 350;
  const PRICE_BATCH_SIZE = Math.max(1, Number.parseInt(config.priceBatchSize || config.price_batch_size || '8', 10) || 8);
  const STORE_CREDIT_ONLY = parseBool(config.storeCreditOnly ?? config.store_credit_only ?? false);
  const shopDomain = () => String(window.Shopify?.shop || config.shop || '').trim().toLowerCase();
  const storageKey = () => `cc-buylist-cart:${shopDomain() || 'unknown-shop'}`;
  const submitKey = () => `cc-buylist-last-submit:${shopDomain() || 'unknown-shop'}`;
  const CURRENCY_SYMBOLS = { GBP: 'GBP', EUR: 'EUR', USD: 'USD' };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const refs = {};
  const cart = new Map();
  let activeFetch = null;
  let activePriceFetches = [];
  let activeHydrationPending = 0;
  let activeSearchToken = 0;
  let activeSetFetch = null;
  let selectedSet = null;
  let currency = String(config.currency || 'GBP').toUpperCase();
  let lastFocused = null;
  let positionFrame = null;

  const esc = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const debounce = (fn, wait = 250) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const money = (value) => `${CURRENCY_SYMBOLS[currency] || currency} ${Number(value || 0).toFixed(2)}`;
  const parseMoney = (value) => Number.parseFloat(String(value ?? '0')) || 0;
  const normalize = (value) => String(value || '').trim();
  const normalizeLower = (value) => normalize(value).toLowerCase();
  const itemKey = (item) => [
    item.cardId,
    item.condition,
    item.finish,
    item.language || 'en'
  ].map(normalizeLower).join('|');

  function hashEmail(email) {
    const text = normalizeLower(email);
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1) {
      hash = ((hash << 5) + hash) + text.charCodeAt(i);
      hash &= 0xffffffff;
    }
    return Math.abs(hash).toString(36);
  }

  function quantity(value) {
    return Math.max(1, Number.parseInt(value, 10) || 1);
  }

  function parseBool(value, fallback = false) {
    if (typeof value === 'boolean') return value;
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number') return value !== 0;
    const text = String(value || '').trim().toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(text)) return true;
    if (['0', 'false', 'no', 'n', 'off'].includes(text)) return false;
    return fallback;
  }

  function offerUnit(offer) {
    return parseMoney(offer?.buylist_price);
  }

  function offerStoreCreditUnit(offer) {
    const buylistPrice = offerUnit(offer);
    const storeCreditPrice = parseMoney(offer?.store_credit_price);
    return storeCreditPrice || buylistPrice;
  }

  function selectedUnitPrice(item, storeCreditSelected = false) {
    const buylistPrice = parseMoney(item?.buylistPrice ?? item?.buylist_price ?? item?.unitPrice);
    if (STORE_CREDIT_ONLY) return buylistPrice;
    if (!storeCreditSelected) return buylistPrice;
    return parseMoney(item?.storeCreditPrice ?? item?.store_credit_price) || buylistPrice;
  }

  function hasStoreCreditUplift(items = cartItems()) {
    return items.some((item) => selectedUnitPrice(item, true) > selectedUnitPrice(item, false));
  }

  function syncPaymentMethodVisibility() {
    if (STORE_CREDIT_ONLY) {
      if (refs.paymentMethod) refs.paymentMethod.hidden = true;
      if (refs.paymentStoreCredit) {
        refs.paymentStoreCredit.checked = true;
        refs.paymentStoreCredit.disabled = true;
      }
      if (refs.paymentStandard) {
        refs.paymentStandard.checked = false;
        refs.paymentStandard.disabled = true;
      }
      return false;
    }
    const available = hasStoreCreditUplift();
    if (refs.paymentMethod) refs.paymentMethod.hidden = !available;
    if (refs.paymentStoreCredit) refs.paymentStoreCredit.disabled = !available;
    if (!available && refs.paymentStandard) refs.paymentStandard.checked = true;
    return available;
  }

  function isStoreCreditSelected() {
    if (STORE_CREDIT_ONLY) return true;
    return hasStoreCreditUplift() && refs.paymentStoreCredit?.checked === true;
  }

  function displayName(card) {
    return normalize(card.display_name || card.card_name || card.name || '');
  }

  function cardNumber(card) {
    return normalize(card.card_number || card.number || '');
  }

  function contentImageUrl(path) {
    const clean = String(path || '')
      .replace(/^\/+/, '')
      .replace(/^media\//i, '');
    return clean ? `${CONTENT_BASE_URL}/media/${encodeURI(clean)}` : '';
  }

  function normalizeImageUrl(value) {
    const raw = normalize(value);
    if (!raw) return '';
    const proxyMatch = raw.match(/^(?:https?:\/\/[^/]+)?\/tools\/cc_buylist\/(?:misc\/)?img\/(.+)$/i);
    if (proxyMatch) return contentImageUrl(proxyMatch[1]);
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^\/?media\//i.test(raw)) return contentImageUrl(raw);
    if (raw.startsWith('/')) return raw;
    return contentImageUrl(raw);
  }

  function cardImageUrl(card) {
    return normalizeImageUrl(card.image_url || card.image || card.image_path || '');
  }

  function offerLabel(item) {
    const parts = [item.condition, item.finish];
    if (item.language) parts.push(String(item.language).toUpperCase());
    return parts.filter(Boolean).join(' / ');
  }

  function cardSubtitle(item) {
    const bits = [];
    if (item.setCode) bits.push(item.setCode);
    if (item.setName) bits.push(item.setName);
    if (item.cardNumber) bits.push(`#${item.cardNumber}`);
    return bits.join(' ');
  }

  function unique(values) {
    return Array.from(new Set(values.map(normalize).filter(Boolean)));
  }

  function findOffer(offers, condition, finish, language) {
    return (offers || []).find((offer) => (
      normalizeLower(offer.condition) === normalizeLower(condition)
      && normalizeLower(offer.finish) === normalizeLower(finish)
      && normalizeLower(offer.language || 'en') === normalizeLower(language || 'en')
    )) || null;
  }

  function englishOffers(offers) {
    return (Array.isArray(offers) ? offers : []).filter((offer) => normalizeLower(offer.language || 'en') === 'en');
  }

  function isNonfoilFinish(value) {
    const token = normalizeLower(value).replace(/[\s_-]+/g, '');
    return token === 'nonfoil' || token.includes('nonfoil') || ['normal', 'regular', 'standard'].includes(token);
  }

  function defaultOffer(offers) {
    const list = Array.isArray(offers) ? offers : [];
    return list.find((offer) => isNonfoilFinish(offer.finish_code || offer.finish)) || list[0] || {};
  }

  function setMessage(message, kind = 'muted') {
    if (!refs.results) return;
    setResultsLayerOpen(true);
    refs.results.hidden = false;
    refs.results.setAttribute('aria-expanded', 'true');
    refs.results.innerHTML = `<div class="buylist-results-message buylist-results-message--${esc(kind)}">${esc(message)}</div>`;
    queuePositionFloatingResults();
  }

  function closeResults() {
    if (!refs.results) return;
    refs.results.hidden = true;
    refs.results.setAttribute('aria-expanded', 'false');
    refs.results.innerHTML = '';
    if ((refs.reviewModal?.hidden ?? true) && (refs.successModal?.hidden ?? true)) {
      document.documentElement.classList.remove('buylist-modal-open');
    }
    if (!refs.setResults || refs.setResults.hidden) setResultsLayerOpen(false);
  }

  function abortPriceFetches() {
    activePriceFetches.forEach((controller) => controller.abort());
    activePriceFetches = [];
    activeHydrationPending = 0;
  }

  function setResultsLayerOpen(open) {
    refs.container?.classList.toggle('buylist-results-open', open);
    refs.section?.classList.toggle('buylist-results-open', open);
    if (open) queuePositionFloatingResults();
  }

  function portalPanel(panel) {
    if (panel && panel.parentElement !== document.body) {
      panel.classList.add('buylist-results-portal');
      document.body.appendChild(panel);
    }
  }

  function portalFloatingPanels() {
    portalPanel(refs.setResults);
    portalPanel(refs.results);
  }

  function queuePositionFloatingResults() {
    if (positionFrame) return;
    positionFrame = window.requestAnimationFrame(() => {
      positionFrame = null;
      positionFloatingResults();
    });
  }

  function positionPanel(panel, anchor, gap, maxPanelHeight, minPanelWidth = 260) {
    if (!panel || panel.hidden || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const margin = 8;
    const panelMinWidth = Math.min(minPanelWidth, Math.max(160, viewportWidth - margin * 2));
    const left = Math.max(margin, Math.min(rect.left, viewportWidth - panelMinWidth - margin));
    const availableRight = Math.min(viewportWidth - margin, Math.max(rect.right, left + panelMinWidth));
    const width = Math.max(panelMinWidth, availableRight - left);
    const top = Math.max(margin, Math.min(rect.bottom + gap, viewportHeight - 80));
    const availableHeight = Math.max(120, viewportHeight - top - margin);
    const height = Math.min(maxPanelHeight, availableHeight);

    panel.style.position = 'fixed';
    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;
    panel.style.right = 'auto';
    panel.style.width = `${Math.round(width)}px`;
    panel.style.maxHeight = `${Math.round(height)}px`;
  }

  function positionSectionPanel(panel, anchor, gap, maxPanelHeight) {
    if (!panel || panel.hidden || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
    const margin = 8;
    const left = Math.max(margin, rect.left);
    const right = Math.min(viewportWidth - margin, rect.right);
    const width = Math.max(160, right - left);
    const top = Math.max(margin, Math.min(rect.bottom + gap, viewportHeight - 80));
    const availableHeight = Math.max(120, viewportHeight - top - margin);
    const height = Math.min(maxPanelHeight, availableHeight);

    panel.style.position = 'fixed';
    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;
    panel.style.right = 'auto';
    panel.style.width = `${Math.round(width)}px`;
    panel.style.maxHeight = `${Math.round(height)}px`;
  }

  function positionFloatingResults() {
    positionPanel(refs.setResults, refs.setContainer || refs.setInput, 8, 320, 260);
    positionSectionPanel(refs.results, refs.toolbar || refs.container, 8, Math.min(window.innerHeight * 0.72, 680));
  }

  function setSetMessage(message, kind = 'muted') {
    if (!refs.setResults) return;
    setResultsLayerOpen(true);
    refs.setResults.hidden = false;
    refs.setResults.setAttribute('aria-expanded', 'true');
    refs.setResults.innerHTML = `<div class="buylist-results-message buylist-results-message--${esc(kind)}">${esc(message)}</div>`;
    queuePositionFloatingResults();
  }

  function closeSetResults() {
    if (!refs.setResults) return;
    refs.setResults.hidden = true;
    refs.setResults.setAttribute('aria-expanded', 'false');
    refs.setResults.innerHTML = '';
    if (!refs.results || refs.results.hidden) setResultsLayerOpen(false);
  }

  function setGameOptions(options, disabled = false) {
    if (!refs.gameFilter) return;
    refs.gameFilter.innerHTML = options
      .map((option) => `<option value="${esc(option.value)}">${esc(option.label)}</option>`)
      .join('');
    refs.gameFilter.disabled = disabled;
  }

  async function loadBuylistGames() {
    if (!refs.gameFilter) return;
    setGameOptions([{ value: '', label: 'Loading games...' }], true);

    const shop = shopDomain();
    if (!shop) {
      setGameOptions([{ value: '', label: 'Shop unavailable' }], true);
      return;
    }

    try {
      const params = new URLSearchParams({ shopify_domain: shop });
      const response = await fetch(`${GAMES_ENDPOINT}?${params.toString()}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || response.statusText);

      const games = Array.isArray(data.games) ? data.games : [];
      const options = games
        .map((game) => ({
          value: normalize(game.value || game.game || game.code),
          label: normalize(game.label || game.name || game.value || game.game || game.code)
        }))
        .filter((game) => game.value && game.label);

      if (!options.length) {
        setGameOptions([{ value: '', label: 'No buylist games enabled' }], true);
        return;
      }

      if (options.length === 1) {
        setGameOptions(options, false);
        refs.gameFilter.value = options[0].value;
        return;
      }

      setGameOptions([
        { value: '', label: 'Select a game' },
        ...options
      ], false);
    } catch (error) {
      console.error('[buylist games]', error);
      setGameOptions([{ value: '', label: 'Unable to load games' }], true);
    }
  }

  function buildSetSearchUrl(query, game) {
    const params = new URLSearchParams({
      shopify_domain: shopDomain(),
      game,
      search: query,
      page_size: '10'
    });
    return `${SETS_ENDPOINT}?${params.toString()}`;
  }

  function buildSearchUrl(query, game, setName, setCode) {
    const params = new URLSearchParams({
      shopify_domain: shopDomain(),
      game,
      search: query,
      page: '1',
      page_size: '32',
      include_prices: 'false'
    });
    if (setCode) params.set('set_code', setCode);
    if (setName) params.set('set_name', setName);
    return `${SEARCH_ENDPOINT}?${params.toString()}`;
  }

  function hasCardSearchTerm(query) {
    return normalize(query).length >= 2;
  }

  function clearCardSearchResults() {
    if (activeFetch) {
      activeFetch.abort();
      activeFetch = null;
    }
    activeSearchToken += 1;
    abortPriceFetches();
    closeResults();
  }

  async function doSearch() {
    const query = normalize(refs.searchInput?.value);
    const setName = normalize(refs.setInput?.value);
    const setCode = selectedSet && normalizeLower(selectedSet.name) === normalizeLower(setName)
      ? normalize(selectedSet.code)
      : '';
    const effectiveQuery = query.length >= 2 ? query : '';
    const effectiveSetName = !setCode && setName.length >= 2 ? setName : '';
    const game = normalize(refs.gameFilter?.value);
    if (!hasCardSearchTerm(effectiveQuery)) {
      clearCardSearchResults();
      return;
    }
    if (!game) {
      setMessage('Select a game before searching.');
      return;
    }
    if (!shopDomain()) {
      setMessage('Shop context is unavailable. Refresh the page and try again.', 'error');
      return;
    }

    if (activeFetch) activeFetch.abort();
    abortPriceFetches();
    const controller = new AbortController();
    const searchToken = Date.now();
    activeSearchToken = searchToken;
    activeFetch = controller;
    setMessage('Searching...');

    try {
      const response = await fetch(buildSearchUrl(effectiveQuery, game, effectiveSetName, setCode), { signal: controller.signal });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || response.statusText);
      if (activeSearchToken !== searchToken) return;
      currency = String(data.currency || currency || 'GBP').toUpperCase();
      const results = Array.isArray(data.results) ? data.results : [];
      renderResults(results, effectiveQuery);
      hydrateResultPrices(results, game, effectiveQuery, searchToken);
      updateTotals();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[buylist search]', error);
      setMessage('Something went wrong. Please try again.', 'error');
    }
  }

  async function doSetSearch() {
    const query = normalize(refs.setInput?.value);
    const game = normalize(refs.gameFilter?.value);
    selectedSet = selectedSet && normalizeLower(selectedSet.name) === normalizeLower(query) ? selectedSet : null;

    if (query.length < 2) {
      closeSetResults();
      return;
    }
    if (!game) {
      setSetMessage('Select a game first.');
      return;
    }
    if (!shopDomain()) {
      setSetMessage('Shop context is unavailable. Refresh the page and try again.', 'error');
      return;
    }

    if (activeSetFetch) activeSetFetch.abort();
    const controller = new AbortController();
    activeSetFetch = controller;
    setSetMessage('Searching sets...');

    try {
      const response = await fetch(buildSetSearchUrl(query, game), { signal: controller.signal });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || response.statusText);
      const sets = Array.isArray(data.sets) ? data.sets : [];
      renderSetResults(sets);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[buylist set search]', error);
      setSetMessage('Unable to search sets. Please try again.', 'error');
    }
  }

  function renderSetResults(sets) {
    if (!sets.length) {
      setSetMessage('No matching sets found.');
      return;
    }

    refs.setResults.hidden = false;
    refs.setResults.setAttribute('aria-expanded', 'true');
    refs.setResults.innerHTML = sets.map((set, index) => setOptionHtml(set, index)).join('');
    queuePositionFloatingResults();
    refs.setResults.querySelectorAll('[data-set-index]').forEach((button) => {
      button.addEventListener('click', () => {
        const set = sets[Number(button.dataset.setIndex)];
        selectedSet = {
          code: normalize(set.code),
          name: normalize(set.name)
        };
        refs.setInput.value = selectedSet.name;
        closeSetResults();
        clearCardSearchResults();
      });
    });
  }

  function setOptionHtml(set, index) {
    const code = normalize(set.code);
    const name = normalize(set.name);
    const releaseDate = normalize(set.release_date);
    return `
      <button type="button" class="buylist-set-option" role="option" data-set-index="${index}">
        <span class="buylist-set-option__name">${esc(name)}</span>
        <span class="buylist-set-option__meta">${esc([code, releaseDate].filter(Boolean).join(' / '))}</span>
      </button>`;
  }

  function renderResults(cards, query) {
    if (!cards.length) {
      setMessage('No matching cards found.');
      return;
    }

    setResultsLayerOpen(true);
    refs.results.hidden = false;
    refs.results.setAttribute('aria-expanded', 'true');
    refs.results.innerHTML = `<div class="buylist-grid" role="list">${cards.map((card, index) => resultCardHtml(card, index, query)).join('')}</div>`;
    queuePositionFloatingResults();

    cards.forEach((card, index) => bindResultCard(card, index));
  }

  function resultCardHtml(card, index, query) {
    const name = displayName(card);
    const number = cardNumber(card);
    const image = cardImageUrl(card);
    const offers = englishOffers(card.offers);
    const isPending = normalizeLower(card.price_status) === 'pending';
    const controlsDisabled = isPending || !offers.length;
    const selected = defaultOffer(offers);
    const priceHtml = offers.length
      ? esc(money(offerUnit(selected)))
      : (isPending ? `${esc(currency)} <span class="buylist-price-skeleton" aria-label="Loading price"></span>` : 'Unavailable');
    const statusText = offers.length || isPending ? '' : 'This card is not currently available.';
    const conditions = unique(offers.map((offer) => offer.condition));
    const finishes = unique(offers.map((offer) => offer.finish));
    const conditionOptions = isPending ? ['Near Mint'] : conditions;
    const finishOptions = isPending ? ['Non-Foil'] : finishes;
    const selectedCondition = isPending ? 'Near Mint' : selected.condition;
    const selectedFinish = isPending ? 'Non-Foil' : selected.finish;
    return `
      <article class="buylist-card" role="listitem" data-result-index="${index}" data-card-id="${esc(card.card_id || card.id || '')}">
        <div class="buylist-card-media">
          ${image ? `<img src="${esc(image)}" alt="${esc(name)}" width="160" height="224" loading="lazy" decoding="async">` : '<div class="buylist-card-thumb" aria-hidden="true"></div>'}
        </div>
        <div class="buylist-card-body">
          <div class="buylist-card-info">
            <h3 class="buylist-card-title">${esc(name)}</h3>
            <p class="buylist-card-meta">${esc(card.set_name || '')} ${card.set_code ? `[${esc(card.set_code)}]` : ''} ${number ? `#${esc(number)}` : ''}</p>
          </div>
          <div class="buylist-card-controls" role="group" aria-label="Offer options">
            ${selectHtml('Condition', 'condition', conditionOptions, selectedCondition, controlsDisabled, 'Unavailable')}
            ${selectHtml('Finish', 'finish', finishOptions, selectedFinish, controlsDisabled, 'Unavailable')}
          </div>
          <div class="buylist-card-actions">
            <strong class="buylist-card-price">${priceHtml}</strong>
            <input class="buylist-qty-input" type="number" inputmode="numeric" min="1" step="1" value="1" aria-label="Quantity">
            <button type="button" class="buylist-add-btn buylist-add-btn--icon" aria-label="Add ${esc(name)}"${offers.length ? '' : ' disabled'}>+</button>
          </div>
          <p class="buylist-offer-status"${offers.length || isPending ? ' hidden' : ''}>${esc(statusText)}</p>
        </div>
      </article>`;
  }

  function selectHtml(label, field, values, selected, disabled = false, placeholder = 'Loading') {
    const options = values.length ? values : [placeholder];
    return `
      <label class="buylist-field">
        <span class="buylist-field-label">${esc(label)}</span>
        <select class="buylist-select" data-offer-field="${esc(field)}"${disabled ? ' disabled' : ''}>
          ${options.map((value) => `<option value="${esc(value)}"${normalizeLower(value) === normalizeLower(selected) ? ' selected' : ''}>${esc(field === 'language' ? value.toUpperCase() : value)}</option>`).join('')}
        </select>
      </label>`;
  }

  function bindResultCard(card, index) {
    const root = refs.results.querySelector(`[data-result-index="${index}"]`);
    if (!root) return;
    const offers = englishOffers(card.offers);
    const selects = $$('[data-offer-field]', root);
    const priceEl = $('.buylist-card-price', root);
    const qtyEl = $('.buylist-qty-input', root);
    const addBtn = $('.buylist-add-btn', root);
    const status = $('.buylist-offer-status', root);

    const selectedValues = () => ({
      condition: $('[data-offer-field="condition"]', root)?.value || '',
      finish: $('[data-offer-field="finish"]', root)?.value || '',
      language: 'en'
    });

    const refresh = () => {
      if (!offers.length) {
        const isPending = normalizeLower(card.price_status) === 'pending';
        if (isPending) {
          priceEl.innerHTML = `${esc(currency)} <span class="buylist-price-skeleton" aria-label="Loading price"></span>`;
          status.hidden = true;
          status.textContent = '';
        } else {
          priceEl.textContent = 'Unavailable';
          status.hidden = false;
          status.textContent = 'This card is not currently available.';
        }
        addBtn.disabled = true;
        qtyEl.disabled = true;
        return null;
      }
      const selected = selectedValues();
      const offer = findOffer(offers, selected.condition, selected.finish, selected.language);
      if (!offer) {
        priceEl.textContent = 'Unavailable';
        addBtn.disabled = true;
        status.hidden = false;
        status.textContent = 'This exact offer is not currently available.';
        return null;
      }
      priceEl.textContent = money(offerUnit(offer));
      addBtn.disabled = false;
      qtyEl.disabled = false;
      status.hidden = true;
      status.textContent = '';
      return offer;
    };

    selects.forEach((select) => select.addEventListener('change', refresh));
    qtyEl.addEventListener('input', () => { qtyEl.value = quantity(qtyEl.value); });
    addBtn.addEventListener('click', () => {
      const selected = selectedValues();
      const offer = findOffer(offers, selected.condition, selected.finish, selected.language);
      if (!offer) return;
      addToCart(card, offer, quantity(qtyEl.value));
      refs.searchInput.value = '';
      closeResults();
    });
    refresh();
  }

  function updateResultCard(card, index, query) {
    const root = refs.results?.querySelector(`[data-result-index="${index}"]`);
    if (!root) return;
    root.outerHTML = resultCardHtml(card, index, query);
    bindResultCard(card, index);
  }

  function removeResultCard(index) {
    refs.results?.querySelector(`[data-result-index="${index}"]`)?.remove();
  }

  function showEmptyIfHydrationComplete(searchToken) {
    if (activeSearchToken !== searchToken || activeHydrationPending > 0) return;
    if (refs.results?.querySelector('.buylist-card')) return;
    setMessage('Not currently purchasing any matching cards.');
  }

  async function hydratePriceBatch(batch, game, query, searchToken) {
    const cardIds = batch.map(({ card }) => normalize(card.card_id || card.id)).filter(Boolean);
    if (!cardIds.length) return;

    const controller = new AbortController();
    activePriceFetches.push(controller);

    try {
      const response = await fetch(PRICE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Shop-Domain': shopDomain()
        },
        body: JSON.stringify({
          shopify_domain: shopDomain(),
          game,
          card_ids: cardIds,
          external: true
        }),
        signal: controller.signal
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || response.statusText);
      if (activeSearchToken !== searchToken) return;

      currency = String(data.currency || currency || 'GBP').toUpperCase();
      const pricedById = new Map((Array.isArray(data.results) ? data.results : [])
        .map((result) => [normalize(result.card_id || result.id), result]));

      batch.forEach(({ card, index }) => {
        const priced = pricedById.get(normalize(card.card_id || card.id));
        const offers = englishOffers(priced?.offers);
        if (!offers.length) {
          removeResultCard(index);
          return;
        }
        updateResultCard({
          ...card,
          offers,
          price_status: priced?.price_status || 'ready'
        }, index, query);
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (activeSearchToken !== searchToken) return;
      console.error('[buylist price hydration]', error);
      batch.forEach(({ index }) => removeResultCard(index));
    } finally {
      activePriceFetches = activePriceFetches.filter((item) => item !== controller);
      if (activeSearchToken === searchToken) {
        activeHydrationPending = Math.max(activeHydrationPending - batch.length, 0);
        showEmptyIfHydrationComplete(searchToken);
      }
    }
  }

  function hydrateResultPrices(cards, game, query, searchToken) {
    const queue = cards
      .map((card, index) => ({ card, index }))
      .filter(({ card }) => normalize(card.card_id || card.id));
    const batches = [];
    for (let cursor = 0; cursor < queue.length; cursor += PRICE_BATCH_SIZE) {
      batches.push(queue.slice(cursor, cursor + PRICE_BATCH_SIZE));
    }
    activeHydrationPending = queue.length;
    showEmptyIfHydrationComplete(searchToken);

    batches.forEach((batch) => {
      if (activeSearchToken === searchToken) hydratePriceBatch(batch, game, query, searchToken);
    });
  }

  function addToCart(card, offer, qty) {
    const buylistPrice = offerUnit(offer);
    const item = {
      cardId: normalize(card.card_id || card.id),
      productType: normalize(card.game || refs.gameFilter?.value),
      productName: displayName(card),
      cardNumber: cardNumber(card),
      setCode: normalize(card.set_code),
      setName: normalize(card.set_name),
      imageUrl: cardImageUrl(card),
      condition: normalize(offer.condition),
      finish: normalize(offer.finish),
      language: normalize(offer.language || 'en'),
      shopifyVariantId: normalize(offer.shopify_variant_id),
      buylistPrice,
      storeCreditPrice: offerStoreCreditUnit(offer),
      unitPrice: buylistPrice,
      quantity: qty
    };
    if (!item.cardId || !item.buylistPrice) return;
    const key = itemKey(item);
    const existing = cart.get(key);
    if (existing) existing.quantity += qty;
    else cart.set(key, item);
    renderCart();
    persistCart();
  }

  function cartItems() {
    return Array.from(cart.values());
  }

  function totalValue(storeCreditSelected = false) {
    return cartItems().reduce((sum, item) => sum + selectedUnitPrice(item, storeCreditSelected) * item.quantity, 0);
  }

  function updateTotals() {
    if (refs.total) refs.total.textContent = `Total: ${money(totalValue(false))}`;
    if (refs.reviewGrand) refs.reviewGrand.textContent = `Total: ${money(totalValue(isStoreCreditSelected()))}`;
  }

  function renderCart() {
    const items = cartItems();
    syncPaymentMethodVisibility();
    refs.inlineSection.hidden = !items.length;
    refs.inlineCount.textContent = `${items.reduce((sum, item) => sum + item.quantity, 0)} items`;
    refs.inlineBody.innerHTML = items.map((item) => rowHtml(item, true)).join('');
    refs.inlineBody.querySelectorAll('[data-cart-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        cart.delete(button.dataset.cartRemove);
        renderCart();
        persistCart();
      });
    });
    refs.inlineBody.querySelectorAll('[data-cart-qty]').forEach((input) => {
      input.addEventListener('change', () => {
        const item = cart.get(input.dataset.cartQty);
        if (!item) return;
        item.quantity = quantity(input.value);
        renderCart();
        persistCart();
      });
    });
    updateTotals();
  }

  function rowHtml(item, editable, storeCreditSelected = false) {
    const key = itemKey(item);
    const unitPrice = selectedUnitPrice(item, storeCreditSelected);
    const total = unitPrice * item.quantity;
    return `
      <tr>
        <td>
          <div class="buylist-item-cell">
            ${item.imageUrl ? `<img src="${esc(item.imageUrl)}" alt="" class="buylist-row-thumb" loading="lazy" decoding="async">` : ''}
            <div>
              <strong>${esc(item.productName)}</strong>
              <span>${esc(cardSubtitle(item))}</span>
            </div>
          </div>
        </td>
        <td>${esc(offerLabel(item))}</td>
        <td>${editable ? `<input class="buylist-qty-input" data-cart-qty="${esc(key)}" type="number" min="1" step="1" value="${item.quantity}" aria-label="Quantity">` : esc(item.quantity)}</td>
        <td>${money(unitPrice)}</td>
        <td>${money(total)}</td>
        ${editable ? `<td><button type="button" class="buylist-remove-btn" data-cart-remove="${esc(key)}" aria-label="Remove ${esc(item.productName)}">&times;</button></td>` : ''}
      </tr>`;
  }

  function renderReviewRows() {
    if (!refs.reviewBody) return;
    refs.reviewBody.innerHTML = cartItems().map((item) => rowHtml(item, false, isStoreCreditSelected())).join('');
    updateTotals();
  }

  function openReviewModal() {
    if (!cart.size) {
      window.alert('Add a card before submitting.');
      return;
    }
    const fullName = [customer?.first_name, customer?.last_name].filter(Boolean).join(' ').trim();
    if (refs.sellerName && !refs.sellerName.value) refs.sellerName.value = fullName;
    if (refs.sellerEmail && !refs.sellerEmail.value) refs.sellerEmail.value = customer?.email || '';
    syncPaymentMethodVisibility();
    renderReviewRows();
    clearFormError();
    openModal(refs.reviewModal);
  }

  function getSeller() {
    const name = normalize(refs.sellerName?.value);
    const email = normalizeLower(refs.sellerEmail?.value);
    if (!name) {
      const error = new Error('Enter your name.');
      error.field = refs.sellerName;
      throw error;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      const error = new Error('Enter a valid email address.');
      error.field = refs.sellerEmail;
      throw error;
    }
    return { name, email };
  }

  function fieldErrorEl(field) {
    if (!field?.id) return null;
    return document.getElementById(`${field.id}-error`);
  }

  function markFieldError(field, message) {
    if (!field) return;
    field.setAttribute('aria-invalid', 'true');
    const errorEl = fieldErrorEl(field);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  function clearFieldError(field) {
    if (!field) return;
    field.removeAttribute('aria-invalid');
    const errorEl = fieldErrorEl(field);
    if (errorEl) errorEl.hidden = true;
  }

  function showFormError(message, field = null) {
    refs.reviewError.textContent = message;
    refs.reviewError.hidden = false;
    if (field) {
      markFieldError(field, message);
      field.focus({ preventScroll: true });
    }
  }

  function clearFormError() {
    refs.reviewError.textContent = '';
    refs.reviewError.hidden = true;
    clearFieldError(refs.sellerName);
    clearFieldError(refs.sellerEmail);
  }

  function referenceToken() {
    const rand = () => Math.random().toString(36).slice(2, 6).toUpperCase();
    return `BL-${rand()}-${rand()}`;
  }

  function cartSignature(items, storeCreditSelected = false) {
    return JSON.stringify({
      storeCreditSelected,
      items: items.map((item) => ({
        id: item.cardId,
        condition: item.condition,
        finish: item.finish,
        language: item.language,
        qty: item.quantity,
        price: selectedUnitPrice(item, storeCreditSelected)
      })).sort((a, b) => `${a.id}${a.condition}${a.finish}`.localeCompare(`${b.id}${b.condition}${b.finish}`))
    });
  }

  function cartFingerprint(signature) {
    let hash = 2166136261;
    for (let i = 0; i < signature.length; i += 1) {
      hash ^= signature.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
  }

  function recentlySubmitted(signature) {
    try {
      const payload = JSON.parse(window.sessionStorage.getItem(submitKey()) || '{}');
      return payload.signature === signature && Date.now() - Number(payload.at || 0) < 5 * 60 * 1000;
    } catch {
      return false;
    }
  }

  function markSubmitted(signature) {
    try {
      window.sessionStorage.setItem(submitKey(), JSON.stringify({ signature, at: Date.now() }));
    } catch {}
  }

  async function postBuylist(endpoint, payload) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Shop-Domain': shopDomain()
      },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(response.statusText || `HTTP ${response.status}`);
    return response;
  }

  async function submitBuylist() {
    let seller;
    try {
      seller = getSeller();
      clearFormError();
    } catch (error) {
      showFormError(error.message, error.field);
      return;
    }

    const items = cartItems();
    syncPaymentMethodVisibility();
    const storeCreditSelected = isStoreCreditSelected();
    const signature = cartSignature(items, storeCreditSelected);
    if (recentlySubmitted(signature)) {
      showFormError('This exact list was submitted recently. Please wait a few minutes before trying again.');
      return;
    }

    const reference = referenceToken();
    const payload = {
      seller,
      customer_id: customer?.id ? String(customer.id) : `guest:${hashEmail(seller.email)}`,
      customer_reference: reference,
      store_credit_selected: storeCreditSelected,
      submission_signature: cartFingerprint(signature),
      items: items.map((item) => ({
        card_id: item.cardId,
        product_type: item.productType,
        card_name: `${item.productName}${item.cardNumber ? ` (${item.cardNumber})` : ''} - ${item.setCode} ${item.setName}`.trim(),
        set_code: item.setCode,
        condition: item.condition,
        finish: item.finish,
        language: item.language,
        shopify_variant_id: item.shopifyVariantId,
        quantity: item.quantity,
        price: selectedUnitPrice(item, storeCreditSelected)
      }))
    };

    refs.confirmBtn.disabled = true;
    try {
      await postBuylist(SUBMIT_ENDPOINT, payload);
      refs.reference.textContent = reference;
      closeModal(refs.reviewModal);
      openModal(refs.successModal);
      cart.clear();
      renderCart();
      persistCart(true);
      markSubmitted(signature);
    } catch (error) {
      console.error('[buylist submit]', error);
      showFormError('Submission failed. Please try again.');
    } finally {
      refs.confirmBtn.disabled = false;
    }
  }

  function persistCart(forceClear = false) {
    try {
      if (forceClear || !cart.size) {
        window.localStorage.removeItem(storageKey());
        return;
      }
      window.localStorage.setItem(storageKey(), JSON.stringify({
        version: STORAGE_VERSION,
        currency,
        items: cartItems(),
        updatedAt: Date.now()
      }));
    } catch {}
  }

  function restoreCart() {
    try {
      const raw = window.localStorage.getItem(storageKey());
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (payload.version !== STORAGE_VERSION || !Array.isArray(payload.items)) return;
      currency = String(payload.currency || currency || 'GBP').toUpperCase();
      payload.items.forEach((item) => {
        if (!item.cardId || !item.productName) return;
        item.quantity = quantity(item.quantity);
        item.buylistPrice = parseMoney(item.buylistPrice ?? item.buylist_price ?? item.unitPrice);
        item.storeCreditPrice = parseMoney(item.storeCreditPrice ?? item.store_credit_price) || item.buylistPrice;
        item.unitPrice = item.buylistPrice;
        item.imageUrl = normalizeImageUrl(item.imageUrl);
        if (!item.buylistPrice) return;
        cart.set(itemKey(item), item);
      });
    } catch {
      window.localStorage.removeItem(storageKey());
    }
  }

  function focusables(root) {
    return $$('a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])', root)
      .filter((el) => !el.hidden && el.offsetParent !== null);
  }

  function openModal(modal) {
    lastFocused = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('buylist-modal-open');
    const first = focusables(modal)[0] || modal;
    setTimeout(() => first.focus({ preventScroll: true }), 0);
  }

  function closeModal(modal) {
    modal.hidden = true;
    document.documentElement.classList.remove('buylist-modal-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      setTimeout(() => lastFocused.focus({ preventScroll: true }), 0);
    }
  }

  function bindModal(modal) {
    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.closest('[data-modal-close]')) {
        closeModal(modal);
      }
    });
    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal(modal);
        return;
      }
      if (event.key !== 'Tab') return;
      const els = focusables(modal);
      if (!els.length) {
        event.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function bindEvents() {
    const debouncedSetSearch = debounce(doSetSearch, SET_SEARCH_DEBOUNCE_MS);

    refs.searchForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      doSearch();
    });
    if (!refs.searchForm) {
      refs.searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        doSearch();
      });
    }
    refs.searchInput.addEventListener('input', clearCardSearchResults);
    refs.searchInput.addEventListener('focus', () => {
      if (refs.results.innerHTML) {
        setResultsLayerOpen(true);
        refs.results.hidden = false;
        queuePositionFloatingResults();
      }
    });
    refs.sellerName.addEventListener('input', () => clearFieldError(refs.sellerName));
    refs.sellerEmail.addEventListener('input', () => clearFieldError(refs.sellerEmail));
    refs.setInput.addEventListener('input', () => {
      clearCardSearchResults();
      debouncedSetSearch();
    });
    refs.setInput.addEventListener('focus', () => {
      if (refs.setResults.innerHTML) {
        setResultsLayerOpen(true);
        refs.setResults.hidden = false;
      }
    });
    refs.gameFilter.addEventListener('change', () => {
      selectedSet = null;
      if (refs.setInput) refs.setInput.value = '';
      closeSetResults();
      clearCardSearchResults();
    });
    refs.reviewBtn.addEventListener('click', openReviewModal);
    refs.cancelBtn.addEventListener('click', () => closeModal(refs.reviewModal));
    refs.confirmBtn.addEventListener('click', submitBuylist);
    refs.paymentStandard?.addEventListener('change', renderReviewRows);
    refs.paymentStoreCredit?.addEventListener('change', renderReviewRows);
    document.addEventListener('click', (event) => {
      const clickedSearchControl = refs.searchContainer?.contains(event.target);
      if (!refs.results.contains(event.target) && !clickedSearchControl && event.target !== refs.setInput) closeResults();
      if (!refs.setResults.contains(event.target) && event.target !== refs.setInput) closeSetResults();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !refs.results.hidden) closeResults();
      if (event.key === 'Escape' && !refs.setResults.hidden) closeSetResults();
    });
    window.addEventListener('resize', queuePositionFloatingResults);
    window.addEventListener('scroll', queuePositionFloatingResults, true);
    bindModal(refs.reviewModal);
    bindModal(refs.successModal);
  }

  function collectRefs() {
    Object.assign(refs, {
      container: $('.buylist-container'),
      gameFilter: $('#gameFilter'),
      setInput: $('#buylist-set-input'),
      setResults: $('#buylist-set-results'),
      searchForm: $('#buylist-search-form'),
      searchInput: $('#buylist-search-input'),
      results: $('#buylist-search-results'),
      total: $('#buylist-total'),
      reviewBtn: $('#buylist-review'),
      inlineSection: $('#buylist-inline-section'),
      inlineCount: $('#buylist-inline-count'),
      inlineBody: $('#buylist-inline-tbody'),
      reviewModal: $('#buylist-review-modal'),
      reviewBody: $('#buylist-review-tbody'),
      reviewGrand: $('#buylist-review-grand'),
      reviewError: $('#buylist-review-error'),
      paymentMethod: $('#buylist-payment-method'),
      paymentStandard: $('#buylist-payment-standard'),
      paymentStoreCredit: $('#buylist-payment-store-credit'),
      sellerName: $('#buylist-seller-name'),
      sellerEmail: $('#buylist-seller-email'),
      cancelBtn: $('#buylist-review-cancel'),
      confirmBtn: $('#buylist-review-confirm'),
      successModal: $('#submission-confirm-modal'),
      reference: $('#buylist-reference-display')
    });
    refs.toolbar = $('.buylist-toolbar', refs.container || document);
    refs.setContainer = $('.buylist-set-container', refs.container || document);
    refs.searchContainer = $('.buylist-search-container', refs.container || document);
    refs.section = refs.container?.closest('.shopify-section--cc-buylist, .shopify-section') || null;
    portalFloatingPanels();
  }

  function resolveCssVars(value, styles) {
    let resolved = normalize(value);
    for (let i = 0; i < 6 && resolved.includes('var('); i += 1) {
      resolved = resolved.replace(/var\(\s*(--[\w-]+)(?:\s*,\s*([^)]+))?\s*\)/g, (_, name, fallback = '') => (
        normalize(styles.getPropertyValue(name)) || normalize(fallback)
      ));
    }
    return resolved;
  }

  function themeColor(value, fallback = '', styles = null) {
    const raw = styles ? resolveCssVars(value, styles) : normalize(value);
    if (!raw) return fallback;
    if (window.CSS?.supports?.('color', raw)) return raw;
    const rgbCandidate = `rgb(${raw})`;
    if (window.CSS?.supports?.('color', rgbCandidate)) return rgbCandidate;
    return fallback;
  }

  function themeGradient(value, fallback, styles = null) {
    const raw = styles ? resolveCssVars(value, styles) : normalize(value);
    if (!raw || raw === 'none') return fallback;
    if (window.CSS?.supports?.('background', raw)) return raw;
    return fallback;
  }

  function parseCssColor(value) {
    const raw = normalize(value);
    if (!raw || !document.body) return null;
    const probe = document.createElement('span');
    probe.style.color = raw;
    if (!probe.style.color) return null;
    probe.style.display = 'none';
    document.body.appendChild(probe);
    const computed = window.getComputedStyle(probe).color;
    probe.remove();
    const match = computed.match(/^rgba?\((.+)\)$/i);
    if (!match) return null;
    const parts = match[1]
      .replace(/\//g, ' ')
      .split(/[\s,]+/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 3) return null;
    const channel = (part) => {
      if (part.endsWith('%')) return Math.round((Number.parseFloat(part) || 0) * 2.55);
      return Number.parseFloat(part) || 0;
    };
    const alpha = (part) => {
      if (!part) return 1;
      if (part.endsWith('%')) return Math.max(0, Math.min(1, (Number.parseFloat(part) || 0) / 100));
      return Math.max(0, Math.min(1, Number.parseFloat(part)));
    };
    return {
      r: Math.max(0, Math.min(255, Math.round(channel(parts[0])))),
      g: Math.max(0, Math.min(255, Math.round(channel(parts[1])))),
      b: Math.max(0, Math.min(255, Math.round(channel(parts[2])))),
      a: alpha(parts[3])
    };
  }

  function cssRgb(color) {
    return `rgb(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)})`;
  }

  function compositeColor(color, base) {
    const alpha = color.a ?? 1;
    return {
      r: color.r * alpha + base.r * (1 - alpha),
      g: color.g * alpha + base.g * (1 - alpha),
      b: color.b * alpha + base.b * (1 - alpha),
      a: 1
    };
  }

  function solidThemeColor(value, fallback = '#ffffff') {
    const color = parseCssColor(value);
    const base = parseCssColor(fallback) || { r: 255, g: 255, b: 255, a: 1 };
    if (!color) return cssRgb(base);
    return cssRgb(color.a >= 0.999 ? color : compositeColor(color, base));
  }

  function mixThemeColors(background, foreground, foregroundWeight) {
    const bg = parseCssColor(background) || { r: 255, g: 255, b: 255, a: 1 };
    const fg = parseCssColor(foreground) || { r: 17, g: 20, b: 24, a: 1 };
    const solidBg = bg.a >= 0.999 ? bg : compositeColor(bg, { r: 255, g: 255, b: 255, a: 1 });
    const solidFg = fg.a >= 0.999 ? fg : compositeColor(fg, solidBg);
    const bgWeight = 1 - foregroundWeight;
    return cssRgb({
      r: solidBg.r * bgWeight + solidFg.r * foregroundWeight,
      g: solidBg.g * bgWeight + solidFg.g * foregroundWeight,
      b: solidBg.b * bgWeight + solidFg.b * foregroundWeight,
      a: 1
    });
  }

  function relativeLuminance(color) {
    const channel = (value) => {
      const normalized = value / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : ((normalized + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
  }

  function contrastRatio(first, second) {
    const firstLum = relativeLuminance(first);
    const secondLum = relativeLuminance(second);
    const lighter = Math.max(firstLum, secondLum);
    const darker = Math.min(firstLum, secondLum);
    return (lighter + 0.05) / (darker + 0.05);
  }

  function readableThemeColor(background, candidates) {
    const bg = parseCssColor(background);
    if (!bg) return normalize(candidates[0]) || '#ffffff';
    const solidBg = bg.a >= 0.999 ? bg : compositeColor(bg, { r: 255, g: 255, b: 255, a: 1 });
    let best = null;
    candidates.forEach((candidate) => {
      const color = parseCssColor(candidate);
      if (!color) return;
      const solidColor = color.a >= 0.999 ? color : compositeColor(color, solidBg);
      const contrast = contrastRatio(solidBg, solidColor);
      if (!best || contrast > best.contrast) {
        best = { color: cssRgb(solidColor), contrast };
      }
    });
    return best?.color || normalize(candidates[0]) || '#ffffff';
  }

  function syncThemeColors() {
    if (!refs.container) return;
    const styles = window.getComputedStyle(refs.container);
    const rootStyles = window.getComputedStyle(document.documentElement);
    const bodyStyles = window.getComputedStyle(document.body);
    const firstThemeColor = (names, fallback) => {
      for (const name of names) {
        const color = themeColor(styles.getPropertyValue(name), '', styles)
          || themeColor(rootStyles.getPropertyValue(name), '', rootStyles);
        if (color) return color;
      }
      return fallback;
    };
    const pageBackground = themeColor(bodyStyles.backgroundColor, '', bodyStyles)
      || themeColor(rootStyles.backgroundColor, '', rootStyles)
      || '#ffffff';
    const background = firstThemeColor(['--color-background', '--color-g-bg'], styles.backgroundColor || pageBackground);
    const solidBackground = solidThemeColor(background, pageBackground);
    const foreground = firstThemeColor(['--color-foreground', '--color-g-text'], styles.color || rootStyles.color || '#111418');
    const button = firstThemeColor(['--color-button', '--color-g-button-bg', '--color-g-accent'], foreground);
    let buttonText = firstThemeColor(['--color-button-text', '--color-g-button-text'], background);
    buttonText = readableThemeColor(button, [
      buttonText,
      background,
      solidBackground,
      foreground,
      '#000000',
      '#ffffff'
    ]);
    const shadow = firstThemeColor(['--color-shadow', '--color-g-text'], foreground);
    const gradient = themeGradient(styles.getPropertyValue('--gradient-background'), background, styles);
    const panel = mixThemeColors(solidBackground, foreground, 0.02);
    const elevated = mixThemeColors(solidBackground, foreground, 0.06);
    const targets = [refs.container, refs.results, refs.setResults, ...$$('.buylist-modal')].filter(Boolean);

    targets.forEach((element) => {
      element.style.setProperty('--cc-theme-bg', background);
      element.style.setProperty('--cc-theme-solid-bg', solidBackground);
      element.style.setProperty('--cc-theme-gradient', gradient);
      element.style.setProperty('--cc-theme-text', foreground);
      element.style.setProperty('--cc-theme-panel', panel);
      element.style.setProperty('--cc-theme-elev', elevated);
      element.style.setProperty('--cc-theme-solid-panel', panel);
      element.style.setProperty('--cc-theme-solid-elev', elevated);
      element.style.setProperty('--cc-theme-button', button);
      element.style.setProperty('--cc-theme-button-text', buttonText);
      element.style.setProperty('--cc-theme-shadow', shadow);
    });
  }

  function init() {
    collectRefs();
    syncThemeColors();
    loadBuylistGames();
    restoreCart();
    renderCart();
    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
